import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  where,
  type DocumentData,
  type Query,
  type QuerySnapshot,
} from "firebase/firestore";
import { useEffect, useState } from "react";
import type { z } from "zod";

import { db } from "integrations/firebase/firestore";

import {
  agendaItemSchema,
  type AgendaItem,
  directoryAttendeeSchema,
  type DirectoryAttendee,
  informationItemSchema,
  type InformationItem,
  partnerSchema,
  type Partner,
  resourceSchema,
  type Resource,
  speakerSchema,
  type Speaker,
  trackSchema,
  type Track,
  venueSchema,
  type Venue,
} from "./schemas";
import { mergeResources } from "./resource-audience";

export type ContentState<T> =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; data: T[] };

export function useSubcollection<T>(
  eventId: string,
  buildQuery: (eventId: string) => Query,
  schema: z.ZodType<T>,
  live = false,
): ContentState<T> {
  const [state, setState] = useState<ContentState<T>>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    const contentQuery = buildQuery(eventId);

    const handleSnapshot = (snapshot: QuerySnapshot<DocumentData>) => {
      if (cancelled) return;
      const data: T[] = [];
      snapshot.forEach((item) => {
        const result = schema.safeParse({ id: item.id, ...item.data() });
        if (result.success) data.push(result.data);
      });
      setState({ status: "success", data });
    };
    const handleError = (error: unknown) => {
      if (cancelled) return;
      setState({
        status: "error",
        error: error instanceof Error ? error.message : "Failed to load",
      });
    };

    if (live) {
      const unsubscribe = onSnapshot(contentQuery, handleSnapshot, handleError);
      return () => {
        cancelled = true;
        unsubscribe();
      };
    }

    void getDocs(contentQuery).then(handleSnapshot).catch(handleError);

    return () => {
      cancelled = true;
    };
  }, [eventId, buildQuery, schema, live]);

  return state;
}

// No orderBy: Firestore drops a document missing the ordered field, and
// `dayKey` is written by a trigger after the create, while an item with no
// `startsAt` never gets one. Ordered by dayKey, a new session stayed off the
// live agenda until the trigger ran, and one with no time never showed at all.
// AgendaSchedule groups and orders client-side instead.
const agendaQuery = (id: string) =>
  query(collection(db, "events", id, "agenda_items"));

const tracksQuery = (id: string) =>
  query(collection(db, "events", id, "tracks"), orderBy("displayOrder"));

const directoryQuery = (id: string) =>
  query(collection(db, "events", id, "directory"), orderBy("name"));

const speakersQuery = (id: string) =>
  query(collection(db, "events", id, "speakers"));

const partnersQuery = (id: string) =>
  query(collection(db, "events", id, "partners"), orderBy("name"));

const venuesQuery = (id: string) =>
  query(collection(db, "events", id, "venues"));

const resourcesQuery = (id: string) =>
  query(collection(db, "events", id, "resources"), orderBy("uploadedAt"));

const informationQuery = (id: string) =>
  query(
    collection(db, "events", id, "information_items"),
    where("isPublished", "==", true),
    orderBy("displayOrder"),
  );

/** A8: one-shot agenda, unordered; see agendaQuery. */
export function useAgenda(eventId: string): ContentState<AgendaItem> {
  return useSubcollection(eventId, agendaQuery, agendaItemSchema);
}

/** P4: live participant agenda, unordered; see agendaQuery. */
export function useLiveAgenda(eventId: string): ContentState<AgendaItem> {
  return useSubcollection(eventId, agendaQuery, agendaItemSchema, true);
}

/** P7: tracks ordered by displayOrder. */
export function useTracks(eventId: string): ContentState<Track> {
  return useSubcollection(eventId, tracksQuery, trackSchema);
}

/**
 * P11: the PII-free attendee directory ordered by name.
 *
 * Everybody on the roster who has not withdrawn, which includes the pending
 * rows of a spreadsheet loaded before the event. The projection decides that;
 * see `attendee_projection.py`.
 */
export function useDirectory(eventId: string): ContentState<DirectoryAttendee> {
  return useSubcollection(eventId, directoryQuery, directoryAttendeeSchema);
}

/**
 * One directory entry, fetched lazily. For surfaces that resolve a single
 * person on selection (the speaker profile sheet), where paying for the whole
 * directory — potentially hundreds of documents — to run one find() would be
 * waste. Null while unfetched, absent, or when attendeeId is null.
 */
export function useDirectoryAttendee(
  eventId: string,
  attendeeId: string | null,
): DirectoryAttendee | null {
  const [attendee, setAttendee] = useState<DirectoryAttendee | null>(null);

  useEffect(() => {
    setAttendee(null);
    if (!attendeeId) return;
    let cancelled = false;
    getDoc(doc(db, "events", eventId, "directory", attendeeId))
      .then((snapshot) => {
        if (cancelled || !snapshot.exists()) return;
        const result = directoryAttendeeSchema.safeParse({
          id: snapshot.id,
          ...snapshot.data(),
        });
        if (result.success) setAttendee(result.data);
      })
      // A missing link is cosmetic; the sheet renders fine without it.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [eventId, attendeeId]);

  return attendee;
}

/** P9: speakers (in-memory sort by display order then name in component). */
export function useSpeakers(eventId: string): ContentState<Speaker> {
  return useSubcollection(eventId, speakersQuery, speakerSchema);
}

/** P10: partners ordered by name. */
export function usePartners(eventId: string): ContentState<Partner> {
  return useSubcollection(eventId, partnersQuery, partnerSchema);
}

/** P14: venues, unordered. */
export function useVenues(eventId: string): ContentState<Venue> {
  return useSubcollection(eventId, venuesQuery, venueSchema);
}

/**
 * P13: the resources this member may see, ordered by uploadedAt.
 *
 * Event admins and platform admins read every resource in one query. An
 * attendee cannot: firestore.rules authorises a listing by its filters, so an
 * unfiltered query is refused outright once any resource is limited to
 * tracks. Theirs is two queries, one per arm of the rule, merged here:
 * `audience == "everyone"`, and `trackIds array-contains` their own track.
 * Neither carries an orderBy, which would need a composite index each.
 *
 * The track is read live from the member's own membership, so a reassignment
 * by an event admin re-runs the queries without a reload. The rules read the
 * same field, so what is listed and what may be opened move together.
 */
export function useVisibleResources(
  eventId: string,
  uid: string,
  seeAll: boolean,
): ContentState<Resource> {
  const [state, setState] = useState<ContentState<Resource>>({
    status: "loading",
  });

  useEffect(() => {
    let cancelled = false;
    let run = 0;
    setState({ status: "loading" });

    const parse = (snapshot: QuerySnapshot<DocumentData>): Resource[] => {
      const data: Resource[] = [];
      snapshot.forEach((item) => {
        const result = resourceSchema.safeParse({
          id: item.id,
          ...item.data(),
        });
        if (result.success) data.push(result.data);
      });
      return data;
    };
    const fail = (error: unknown) => {
      if (cancelled) return;
      setState({
        status: "error",
        error: error instanceof Error ? error.message : "Failed to load",
      });
    };

    if (seeAll) {
      getDocs(resourcesQuery(eventId))
        .then((snapshot) => {
          if (!cancelled)
            setState({ status: "success", data: parse(snapshot) });
        })
        .catch(fail);
      return () => {
        cancelled = true;
      };
    }

    const resources = collection(db, "events", eventId, "resources");
    let currentTrack: string | null | undefined;
    const load = (trackId: string | null) => {
      const thisRun = ++run;
      const queries = [
        getDocs(query(resources, where("audience", "==", "everyone"))),
        ...(trackId
          ? [
              getDocs(
                query(resources, where("trackIds", "array-contains", trackId)),
              ),
            ]
          : []),
      ];
      Promise.all(queries)
        .then((snapshots) => {
          if (cancelled || thisRun !== run) return;
          setState({
            status: "success",
            data: mergeResources(...snapshots.map(parse)),
          });
        })
        .catch((error: unknown) => {
          if (thisRun === run) fail(error);
        });
    };

    const unsubscribe = onSnapshot(
      doc(db, "events", eventId, "members", uid),
      (snapshot) => {
        const raw: unknown = snapshot.data()?.trackId;
        const trackId = typeof raw === "string" && raw !== "" ? raw : null;
        if (trackId === currentTrack) return;
        currentTrack = trackId;
        load(trackId);
      },
      fail,
    );
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [eventId, uid, seeAll]);

  return state;
}

/** ID-B3: published information items ordered by displayOrder. */
export function useInformation(eventId: string): ContentState<InformationItem> {
  return useSubcollection(eventId, informationQuery, informationItemSchema);
}
